import { Loader } from "lucide-react";
import React, { useState, useRef, useEffect, useImperativeHandle } from "react";
import { FaPlay } from "react-icons/fa";

interface MediaProps {
    src: string;
    type: "image" | "video";
    alt?: string;
    isMobile: boolean;
    onPlay?: () => void;
    thumbnail?: string;
    isProcessing?: boolean;
}

const Media = React.memo(
    React.forwardRef<HTMLVideoElement | HTMLImageElement, MediaProps>(
        (
            { src, type, alt = "Post media", isMobile, onPlay, thumbnail, isProcessing },
            forwardedRef
        ) => {
            const [playVideo, setPlayVideo] = useState(false);
            const videoRef = useRef<HTMLVideoElement>(null);

            useEffect(() => {
                if (playVideo) {
                    videoRef.current?.play();
                }
            }, [playVideo]);

            useImperativeHandle(forwardedRef, () => videoRef.current!, []);

            if (type === "image") {
                return (
                    <img
                        ref={forwardedRef as React.Ref<HTMLImageElement>}
                        src={src}
                        alt={alt}
                        className="w-full h-full object-contain"
                        loading="lazy"
                    />
                );
            }

            if (!playVideo) {
                return (
                    <div className="relative w-full h-full">
                        <img
                            src={thumbnail}
                            alt={alt}
                            className="w-full h-full object-contain"
                        />

                        {/* Play button */}
                        <button
                            className="absolute inset-0 flex items-center justify-center"
                            onClick={() => {
                                setPlayVideo(true);
                                onPlay?.();
                            }}
                            aria-label="Play video"
                        >
                            <FaPlay className="text-white text-4xl" />
                        </button>

                        {/* Processing overlay */}
                        {isProcessing && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                                <span className="text-white text-sm font-medium">
                                    Video Processing
                                </span>
                                <Loader className="ml-2 animate-spin text-secondary" />
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <video
                    ref={videoRef}
                    src={src}
                    className="w-full h-full object-contain"
                    muted
                    controls
                    autoPlay
                    playsInline
                    preload="none"
                    onPlay={onPlay}
                />
            );
        }
    )
);

Media.displayName = "Media";
export default Media;
